/**
 * AffiliateDashboard.jsx
 *
 * Logged-in approved affiliates see their performance + their payment
 * code at /affiliates/dashboard. Auto-links to the affiliate row via
 * the email address on their Linguistic Quest user account.
 *
 * If the affiliate hasn't been approved yet (still pending or rejected),
 * we show an empathetic state rather than a 404.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Wallet, Copy, Check, TrendingUp, AlertCircle, ExternalLink } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth } from '../contexts/AuthContext';

export default function AffiliateDashboard() {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (!user) return;
        api.get('/affiliates/me')
            .then(({ data }) => setData(data))
            .catch(e => setErr(e?.response?.data?.detail || 'No affiliate profile linked'));
    }, [user]);

    if (!user) {
        return (
            <main className="max-w-2xl mx-auto px-4 py-14 text-center">
                <p>Please <Link to="/login?next=/affiliates/dashboard" className="text-lq-purple-700 hover:underline">sign in</Link> to view your affiliate dashboard.</p>
            </main>
        );
    }
    if (err) {
        return (
            <main className="max-w-2xl mx-auto px-4 py-14" data-testid="affiliate-dashboard-empty">
                <div className="bg-lq-warn/10 border border-lq-warn/30 rounded-2xl p-6 text-center">
                    <AlertCircle size={36} className="mx-auto text-lq-warn mb-2"/>
                    <h2 className="font-display text-2xl mb-2">No affiliate profile here yet</h2>
                    <p className="text-lq-ink-700 mb-4">{err}</p>
                    <Link to="/affiliates/apply" className="inline-flex px-5 py-2 rounded-full bg-lq-purple-500 text-white font-semibold hover:bg-lq-purple-700">Apply to join</Link>
                </div>
            </main>
        );
    }
    if (!data) return <main className="max-w-3xl mx-auto px-4 py-14 text-center text-lq-ink-500">Loading your dashboard…</main>;

    const a = data.affiliate;
    const m = data.metrics || {};

    if (a.status === 'pending') {
        return (
            <main className="max-w-2xl mx-auto px-4 py-14" data-testid="affiliate-dashboard-pending">
                <div className="bg-lq-sky-100 border border-lq-sky-300 rounded-2xl p-6 text-center">
                    <h2 className="font-display text-2xl mb-2">Application pending</h2>
                    <p className="text-lq-ink-700">We review every application by hand. Usually within 48 hours. We'll email you the moment you're approved.</p>
                </div>
            </main>
        );
    }
    if (a.status === 'rejected' || a.status === 'suspended') {
        return (
            <main className="max-w-2xl mx-auto px-4 py-14">
                <div className="bg-lq-error/10 border border-lq-error/30 rounded-2xl p-6 text-center">
                    <h2 className="font-display text-2xl mb-2">Account {a.status}</h2>
                    <p className="text-lq-ink-700">Please email <a href="mailto:hello@linguisticquest.com" className="underline">hello@linguisticquest.com</a> if you think this is a mistake.</p>
                </div>
            </main>
        );
    }

    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const shareUrl = `${origin}/?ref=${a.code}`;
    const copyShare = () => {
        navigator.clipboard.writeText(shareUrl);
        setCopied(true); setTimeout(() => setCopied(false), 1500);
    };

    const pendingDisplay = `£${(m.pending_pence / 100).toFixed(2)}`;
    const paidDisplay = `£${(m.paid_pence / 100).toFixed(2)}`;
    const ableToPayout = m.pending_pence >= m.min_payout_pence;

    return (
        <main className="max-w-5xl mx-auto px-4 py-10" data-testid="affiliate-dashboard">
            <header className="mb-6">
                <h1 className="font-display text-4xl text-lq-ink-900">Hi {a.name.split(' ')[0]} — your affiliate dashboard</h1>
                <p className="mt-2 text-lq-ink-700">10% commission on every Polyglot or Global subscription that uses your code.</p>
            </header>

            {/* Hero — code + share */}
            <section className="bg-gradient-to-br from-lq-purple-500 to-lq-purple-700 text-white rounded-3xl p-6 mb-6 shadow-xl">
                <div className="flex items-center justify-between flex-wrap gap-4">
                    <div>
                        <div className="text-xs uppercase tracking-wider opacity-80">Your code</div>
                        <div className="font-mono text-4xl font-bold" data-testid="affiliate-code">{a.code}</div>
                    </div>
                    <div className="flex flex-col gap-2 max-w-md w-full">
                        <div className="text-xs uppercase tracking-wider opacity-80">Share link</div>
                        <div className="flex items-center gap-2 bg-white/10 rounded-lg px-3 py-2">
                            <code className="flex-1 text-xs font-mono truncate" data-testid="affiliate-share-url">{shareUrl}</code>
                            <button onClick={copyShare} className="bg-white text-lq-purple-700 rounded-full px-3 py-1 text-xs font-semibold hover:bg-lq-purple-50 inline-flex items-center gap-1" data-testid="affiliate-copy-link">
                                {copied ? <Check size={12}/> : <Copy size={12}/>}
                                {copied ? 'Copied' : 'Copy'}
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* Funnel stats */}
            <section className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                <StatCard label="Clicks" value={m.clicks} icon={<TrendingUp size={16}/>}/>
                <StatCard label="Sign-ups" value={m.signups} sub="people made an account"/>
                <StatCard label="Active subs" value={m.active_subscribers} sub="paying or in trial" accent="text-lq-success"/>
                <StatCard label="Conversion" value={m.clicks ? `${Math.round((m.active_subscribers / m.clicks) * 100)}%` : '—'} sub="clicks → active"/>
            </section>

            {/* Earnings */}
            <section className="bg-white rounded-2xl border border-lq-ink-100 p-5 mb-6" data-testid="affiliate-earnings">
                <h2 className="font-display text-2xl text-lq-ink-900 mb-3 inline-flex items-center gap-2"><Wallet size={20}/> Earnings</h2>
                <div className="grid sm:grid-cols-3 gap-3">
                    <StatCard label="Pending payout" value={pendingDisplay} sub={`Min £${(m.min_payout_pence/100).toFixed(0)} to disburse`} accent="text-lq-warn"/>
                    <StatCard label="Paid to date" value={paidDisplay} sub="lifetime via PayPal" accent="text-lq-success"/>
                    <StatCard label="PayPal email" value={<span className="text-sm font-mono">{a.paypal_email}</span>} sub="payouts arrive here"/>
                </div>
                <div className={`mt-4 p-3 rounded-lg ${ableToPayout ? 'bg-lq-success/10 border border-lq-success/30 text-lq-success' : 'bg-lq-ink-100 text-lq-ink-700'}`}>
                    <div className="text-sm">
                        {ableToPayout
                            ? <span><Sparkles size={14} className="inline mr-1"/> You've crossed the £20 threshold — your payout will go out in the next monthly batch.</span>
                            : <span>£{((m.min_payout_pence - m.pending_pence) / 100).toFixed(2)} more pending and we'll disburse via PayPal automatically.</span>}
                    </div>
                </div>
            </section>

            {/* Resources */}
            <section className="bg-lq-paper border border-lq-ink-100 rounded-2xl p-5" data-testid="affiliate-resources">
                <h2 className="font-display text-xl text-lq-ink-900 mb-3">Resources for your audience</h2>
                <ul className="text-sm text-lq-ink-700 space-y-2 list-disc pl-5">
                    <li>Affiliate code applies to <strong>Polyglot and Global</strong> only. Explorer + free tier do not qualify.</li>
                    <li>Commissions are <strong>10% of each successful billing</strong>, paid in pence equivalents.</li>
                    <li>The first 60 days from a referred customer's first payment are a holding window — commission accrued in that window is held until day 60 to protect against refunds and chargebacks.</li>
                    <li>If a referred customer cancels in the first 60 days, the related commission is clawed back. After day 60 it's locked in.</li>
                    <li>Payouts run monthly via PayPal once your pending balance crosses £20.</li>
                    <li>If your balance sits below £20 for <strong>45 days</strong>, you can request release of the residual.</li>
                </ul>
                <div className="mt-4 text-xs text-lq-ink-500">Questions? Email <a href="mailto:hello@linguisticquest.com" className="underline">hello@linguisticquest.com</a> and we'll get back within 24h. Full terms in the <Link to="/legal/affiliate-agreement" className="underline text-lq-purple-700" data-testid="dashboard-terms-link">Affiliate Programme Agreement</Link>.</div>
            </section>
        </main>
    );
}

const StatCard = ({ label, value, sub, icon, accent }) => (
    <div className="bg-white rounded-xl border border-lq-ink-100 p-4">
        <div className="text-[10px] uppercase tracking-wider text-lq-ink-500 inline-flex items-center gap-1">{icon}{label}</div>
        <div className={`mt-1 font-display text-2xl ${accent || 'text-lq-ink-900'}`}>{value}</div>
        {sub && <div className="text-[11px] text-lq-ink-500 mt-1">{sub}</div>}
    </div>
);
